import { useEffect, useRef, useState } from "react";
import { useMap } from "react-map-gl";

export function useOpenRoadsPopup()
{
    const { current: map } = useMap();
    const hoverTimeout = useRef<NodeJS.Timeout | null>( null );
    const [ popup, setPopup ] = useState<null | {
        latitude: number,
        longitude: number,
        text: string,
        description: string,
        imageUrl: string,
        copyAddress: string,
    }>( null );

    // Track the last feature ID shown to avoid re-setting the same popup repeatedly
    const lastFeatureId = useRef<string | number | null>( null );

    useEffect( () =>
    {
        const mapInstance = map?.getMap?.();
        if ( !mapInstance ) return;

        const handleMouseMove = ( e: mapboxgl.MapMouseEvent & mapboxgl.EventData ) =>
        {
            const features = mapInstance.queryRenderedFeatures( e.point, {
                layers: [ "open-roads-layer" ],
            } );

            if ( features.length )
            {
                const f = features[0];
                const props = f.properties;
                const featureId = props?.id !== undefined ? props.id : props?.storeName;

                if ( lastFeatureId.current !== featureId )
                {
                    lastFeatureId.current = featureId;

                    // @ts-ignore
                    const [ lng, lat ] = f.geometry.coordinates;

                    setPopup( {
                        latitude: lat,
                        longitude: lng,
                        text: props?.storeName,
                        description: props?.address,
                        imageUrl: props?.imageUrl,
                        copyAddress: props?.copyAddress,
                    } );

                    mapInstance.getCanvas().style.cursor = "pointer";
                    if ( hoverTimeout.current ) clearTimeout( hoverTimeout.current );
                }
            }
            else
            {
                mapInstance.getCanvas().style.cursor = "";
                if ( !hoverTimeout.current )
                {
                    hoverTimeout.current = setTimeout( () =>
                    {
                        setPopup( null );
                        lastFeatureId.current = null;
                    }, 50 );
                }
            }
        };

        const handleMouseLeave = () =>
        {
            hoverTimeout.current = setTimeout( () =>
            {
                setPopup( null );
                lastFeatureId.current = null;
                mapInstance.getCanvas().style.cursor = "";
            }, 50 );
        };

        mapInstance.on( "mousemove", "open-roads-layer", handleMouseMove );
        mapInstance.on( "mouseleave", "open-roads-layer", handleMouseLeave );

        return () =>
        {
            mapInstance.off( "mousemove", "open-roads-layer", handleMouseMove );
            mapInstance.off( "mouseleave", "open-roads-layer", handleMouseLeave );
        };
    }, [ map ] );

    return { popup, setPopup, hoverTimeout };
}
